import React from 'react';
import type { DerivedSiteVerdict } from '../../lib/portfolio/deriveSiteVerdict';
import { bandColorClass, bandToneClass } from './verdictVisuals';

export interface VerdictPillProps {
  verdict: DerivedSiteVerdict | null;
  uncomputedMode?: 'neutral' | 'hidden';
  showComposite?: boolean;
  tooltip?: string;
  className?: string;
  'data-testid'?: string;
}

export const VerdictPill: React.FC<VerdictPillProps> = ({
  verdict,
  uncomputedMode = 'neutral',
  showComposite = true,
  tooltip,
  className = '',
  'data-testid': testId,
}) => {
  const uncomputed = !verdict || verdict.band == null;
  if (uncomputed && uncomputedMode === 'hidden') return null;

  if (uncomputed) {
    // Distinguish "no composite yet" (—) from "computed but data inadequate"
    // (the adequacy gate sets bandLabel 'Insufficient data'). Both stay neutral
    // grey — never a premature red — but show the more honest message.
    const hasAdequacyLabel = !!verdict && !!verdict.bandLabel && verdict.bandLabel !== '—';
    const label = (hasAdequacyLabel ? verdict!.bandLabel : 'Not yet computed').toUpperCase();
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border-subtle bg-text-muted/20 text-text-muted type-meta uppercase tracking-widest ${className}`.trim()}
        title={tooltip ?? verdict?.adequacyReason ?? 'Not yet computed'}
        data-testid={testId ?? 'verdict-pill-uncomputed'}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border-subtle/60 ${bandColorClass(verdict.bandColor)} ${bandToneClass(verdict.bandColor)} type-meta uppercase tracking-widest ${className}`.trim()}
      title={tooltip ?? `${verdict.bandLabel} · ${verdict.composite?.toFixed(2) ?? '—'}`}
      data-testid={testId ?? `verdict-pill-${verdict.band}`}
    >
      <span>{verdict.bandLabel}</span>
      {showComposite ? <span>{verdict.composite?.toFixed(2) ?? '—'}</span> : null}
    </span>
  );
};
